(() => {
  'use strict';

  if (window.PaulissenHomeOpticalCenterV5Ready) return;
  window.PaulissenHomeOpticalCenterV5Ready = true;

  const CACHE_PREFIX = 'pw-home-optical-v5:';
  const SAMPLE_STEP = 2;
  const TARGET_FILL = 0.50;
  const EDGE_TRIM_RATIO = 0.0025;
  const boundsByImage = new WeakMap();
  const observerByContainer = new WeakMap();

  const numberFromStyle = (style, property) => {
    const value = Number.parseFloat(style[property]);
    return Number.isFinite(value) ? value : 0;
  };

  const render = (img, bounds) => {
    const container = img.closest('.pw-home-category-card__visual');
    if (!container || !bounds) return;

    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    if (!containerWidth || !containerHeight) return;

    const style = window.getComputedStyle(container);
    const paddingLeft = numberFromStyle(style, 'paddingLeft');
    const paddingRight = numberFromStyle(style, 'paddingRight');
    const paddingTop = numberFromStyle(style, 'paddingTop');
    const paddingBottom = numberFromStyle(style, 'paddingBottom');
    const availableWidth = Math.max(1, containerWidth - paddingLeft - paddingRight);
    const availableHeight = Math.max(1, containerHeight - paddingTop - paddingBottom);
    const targetCenterX = paddingLeft + availableWidth / 2;
    const targetCenterY = paddingTop + availableHeight / 2;

    const artworkWidth = Math.max(1, bounds.maxX - bounds.minX + 1);
    const artworkHeight = Math.max(1, bounds.maxY - bounds.minY + 1);
    const scale = Math.min(
      (availableWidth * TARGET_FILL) / artworkWidth,
      (availableHeight * TARGET_FILL) / artworkHeight
    );

    const artworkCenterX = (bounds.minX + bounds.maxX) / 2;
    const artworkCenterY = (bounds.minY + bounds.maxY) / 2;

    img.style.width = `${bounds.width * scale}px`;
    img.style.height = `${bounds.height * scale}px`;
    img.style.left = `${targetCenterX - artworkCenterX * scale}px`;
    img.style.top = `${targetCenterY - artworkCenterY * scale}px`;
    img.style.transform = 'none';
    img.dataset.pwOpticalCentered = 'true';
  };

  const readCache = (src) => {
    try {
      const raw = window.sessionStorage.getItem(`${CACHE_PREFIX}${src}`);
      if (!raw) return null;
      const value = JSON.parse(raw);
      const keys = ['width', 'height', 'minX', 'maxX', 'minY', 'maxY'];
      return keys.every((key) => Number.isFinite(value?.[key])) ? value : null;
    } catch (_) {
      return null;
    }
  };

  const writeCache = (src, bounds) => {
    try {
      window.sessionStorage.setItem(`${CACHE_PREFIX}${src}`, JSON.stringify(bounds));
    } catch (_) {
      // The calculation remains valid for the current page when storage is unavailable.
    }
  };

  const trimmedEdge = (counts, total, fromStart) => {
    const threshold = total * EDGE_TRIM_RATIO;
    let accumulated = 0;

    if (fromStart) {
      for (let index = 0; index < counts.length; index += 1) {
        accumulated += counts[index];
        if (accumulated >= threshold) return index;
      }
      return 0;
    }

    for (let index = counts.length - 1; index >= 0; index -= 1) {
      accumulated += counts[index];
      if (accumulated >= threshold) return index;
    }
    return counts.length - 1;
  };

  const calculateBounds = (probe) => {
    const width = probe.naturalWidth || 512;
    const height = probe.naturalHeight || 512;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return null;

    context.drawImage(probe, 0, 0, width, height);
    const pixels = context.getImageData(0, 0, width, height).data;
    const columnCounts = new Float64Array(width);
    const rowCounts = new Float64Array(height);
    let totalInk = 0;

    for (let y = 0; y < height; y += SAMPLE_STEP) {
      for (let x = 0; x < width; x += SAMPLE_STEP) {
        const index = (y * width + x) * 4;
        const red = pixels[index];
        const green = pixels[index + 1];
        const blue = pixels[index + 2];
        const alpha = pixels[index + 3];
        if (alpha < 20) continue;

        const distanceFromWhite = Math.max(255 - red, 255 - green, 255 - blue);
        const colourSpread = Math.max(red, green, blue) - Math.min(red, green, blue);
        if (distanceFromWhite < 17 && colourSpread < 14) continue;

        const weight = Math.max(1, distanceFromWhite / 24, colourSpread / 20);
        columnCounts[x] += weight;
        rowCounts[y] += weight;
        totalInk += weight;
      }
    }

    if (totalInk <= 0) return null;

    let minX = trimmedEdge(columnCounts, totalInk, true);
    let maxX = trimmedEdge(columnCounts, totalInk, false);
    let minY = trimmedEdge(rowCounts, totalInk, true);
    let maxY = trimmedEdge(rowCounts, totalInk, false);

    minX = Math.max(0, minX - SAMPLE_STEP);
    maxX = Math.min(width - 1, maxX + SAMPLE_STEP);
    minY = Math.max(0, minY - SAMPLE_STEP);
    maxY = Math.min(height - 1, maxY + SAMPLE_STEP);

    if (maxX <= minX || maxY <= minY) return null;
    return { width, height, minX, maxX, minY, maxY };
  };

  const observeContainer = (img) => {
    const container = img.closest('.pw-home-category-card__visual');
    if (!container || observerByContainer.has(container) || !('ResizeObserver' in window)) return;

    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(() => {
        const currentImage = container.querySelector('[data-pw-optical-center]');
        const bounds = currentImage ? boundsByImage.get(currentImage) : null;
        if (currentImage && bounds) render(currentImage, bounds);
      });
    });
    observer.observe(container);
    observerByContainer.set(container, observer);
  };

  const measure = (img) => {
    const src = img.currentSrc || img.src;
    if (!src) return;

    const cached = readCache(src);
    if (cached) {
      boundsByImage.set(img, cached);
      render(img, cached);
      observeContainer(img);
      return;
    }

    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.decoding = 'async';
    probe.onload = () => {
      try {
        const bounds = calculateBounds(probe);
        if (!bounds) return;
        boundsByImage.set(img, bounds);
        writeCache(src, bounds);
        render(img, bounds);
        observeContainer(img);
      } catch (error) {
        console.debug('Optisch centreren van het homepage-icoon is niet beschikbaar.', error);
      }
    };
    probe.src = src;
  };

  const initialize = (root = document) => {
    root.querySelectorAll?.('[data-pw-optical-center]').forEach((img) => {
      img.dataset.pwOpticalCentered = 'pending';
      if (img.complete && img.naturalWidth) measure(img);
      else img.addEventListener('load', () => measure(img), { once: true });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
